import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  TrendingUp, Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Package, Building2, Loader2, Check, X, AlertCircle, Filter,
} from 'lucide-react';

const PRIMARY = '#0099FF';
const ITEMS_PER_PAGE = 20;

const FILTER_TABS = [
  { key: 'todas', label: 'Todas' },
  { key: 'pendiente', label: 'Pendientes' },
  { key: 'aprobada', label: 'Aprobadas' },
  { key: 'rechazada', label: 'Rechazadas' },
];

const ESTADO_BADGES = {
  pendiente:  { label: 'Pendiente',  bg: 'bg-amber-100',  text: 'text-amber-800' },
  aprobada:   { label: 'Aprobada',   bg: 'bg-green-100',  text: 'text-green-700' },
  rechazada:  { label: 'Rechazada',  bg: 'bg-red-100',    text: 'text-red-700' },
};

const getEstadoBadge = (estado) => {
  return ESTADO_BADGES[estado] || { label: estado || '—', bg: 'bg-gray-100', text: 'text-gray-600' };
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' \u20AC';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const AdminOfertas = () => {
  const { token, API_URL } = useAuth();

  const [offers, setOffers] = useState([]);
  const [totalOffers, setTotalOffers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [activeTab, setActiveTab] = useState('pendiente');
  const [page, setPage] = useState(1);

  // Expanded row
  const [expandedOffer, setExpandedOffer] = useState(null);
  const [expandedDetails, setExpandedDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Actions
  const [actionLoading, setActionLoading] = useState(null); // offerId being acted on
  const [rejectModal, setRejectModal] = useState(null); // offerId to reject
  const [rejectMotivo, setRejectMotivo] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  const totalPages = Math.ceil(totalOffers / ITEMS_PER_PAGE);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(ITEMS_PER_PAGE),
      });
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      if (activeTab !== 'todas') params.append('estado', activeTab);

      const res = await fetch(`${API_URL}/ofertas?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar ofertas');
      const data = await res.json();
      setOffers(data.ofertas || []);
      setTotalOffers(data.total || 0);
    } catch (err) {
      console.error('Error fetching offers:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, page, searchTerm, activeTab]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      setSearchTerm(val);
      setPage(1);
    }, 400));
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPage(1);
    setExpandedOffer(null);
  };

  const toggleExpand = async (offerId) => {
    if (expandedOffer === offerId) {
      setExpandedOffer(null);
      setExpandedDetails([]);
      return;
    }
    setExpandedOffer(offerId);
    setLoadingDetails(true);
    try {
      const res = await fetch(`${API_URL}/ofertas/${offerId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar detalle');
      const data = await res.json();
      setExpandedDetails(data.detalle || data.oferta?.detalles || []);
    } catch (err) {
      console.error('Error fetching offer detail:', err);
      setExpandedDetails([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  // ── Approve ──
  const handleApprove = async (offerId) => {
    setActionLoading(offerId);
    setActionError('');
    try {
      const res = await fetch(`${API_URL}/ofertas/${offerId}/aprobar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Error al aprobar la oferta');
      }
      setActionSuccess(`Oferta #${offerId} aprobada correctamente`);
      setTimeout(() => setActionSuccess(''), 4000);
      fetchOffers();
      if (expandedOffer === offerId) {
        setExpandedOffer(null);
        setExpandedDetails([]);
      }
    } catch (err) {
      setActionError(err.message);
      setTimeout(() => setActionError(''), 4000);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Reject ──
  const handleReject = async () => {
    if (!rejectModal) return;
    setActionLoading(rejectModal);
    setActionError('');
    try {
      const res = await fetch(`${API_URL}/ofertas/${rejectModal}/rechazar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ motivo: rejectMotivo }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Error al rechazar la oferta');
      }
      setActionSuccess(`Oferta #${rejectModal} rechazada`);
      setTimeout(() => setActionSuccess(''), 4000);
      setRejectModal(null);
      setRejectMotivo('');
      fetchOffers();
      if (expandedOffer === rejectModal) {
        setExpandedOffer(null);
        setExpandedDetails([]);
      }
    } catch (err) {
      setActionError(err.message);
      setTimeout(() => setActionError(''), 4000);
    } finally {
      setActionLoading(null);
    }
  };

  // Group details by client
  const groupByClient = (details) => {
    const groups = {};
    details.forEach(item => {
      const clientKey = item.cliente_id || 'unknown';
      const clientName = item.cliente_nombre || item.razon_social || `Cliente #${clientKey}`;
      if (!groups[clientKey]) {
        groups[clientKey] = { clientName, items: [] };
      }
      groups[clientKey].items.push(item);
    });
    return Object.values(groups);
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      {/* Page Header */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
          <TrendingUp className="w-6 h-6" style={{ color: PRIMARY }} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Gestion de Ofertas</h1>
          <p className="text-sm text-gray-500">Revisa y aprueba las ofertas comerciales</p>
        </div>
      </div>

      {/* Action feedback */}
      {actionSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm text-green-700">
          <Check size={16} className="flex-shrink-0" />
          {actionSuccess}
          <button onClick={() => setActionSuccess('')} className="ml-auto text-green-400 hover:text-green-600">
            <X size={14} />
          </button>
        </div>
      )}
      {actionError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
          <AlertCircle size={16} className="flex-shrink-0" />
          {actionError}
          <button onClick={() => setActionError('')} className="ml-auto text-red-400 hover:text-red-600">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Filter tabs + Search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 ${
                activeTab === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar por comercial o ID..."
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10"></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N Oferta</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comercial</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Líneas</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{ borderColor: PRIMARY }} />
                  </td>
                </tr>
              ) : offers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No se encontraron ofertas</p>
                  </td>
                </tr>
              ) : (
                offers.map(offer => {
                  const badge = getEstadoBadge(offer.estado);
                  const isExpanded = expandedOffer === offer.id;
                  const isPending = offer.estado === 'pendiente';
                  return (
                    <React.Fragment key={offer.id}>
                      <tr
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => toggleExpand(offer.id)}
                      >
                        <td className="px-6 py-4">
                          {isExpanded
                            ? <ChevronUp size={16} className="text-gray-400" />
                            : <ChevronDown size={16} className="text-gray-400" />
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{offer.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {offer.comercial_nombre || offer.comercial || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(offer.fecha_creacion)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                          {offer.total_items || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                          {isPending && (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleApprove(offer.id)}
                                disabled={actionLoading === offer.id}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors"
                              >
                                {actionLoading === offer.id ? (
                                  <Loader2 size={12} className="animate-spin" />
                                ) : (
                                  <Check size={12} />
                                )}
                                Aprobar
                              </button>
                              <button
                                onClick={() => { setRejectModal(offer.id); setRejectMotivo(''); }}
                                disabled={actionLoading === offer.id}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
                              >
                                <X size={12} />
                                Rechazar
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <tr>
                          <td colSpan="7" className="px-6 py-4 bg-gray-50">
                            {loadingDetails ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 size={20} className="animate-spin text-gray-400" />
                                <span className="ml-2 text-sm text-gray-500">Cargando detalle...</span>
                              </div>
                            ) : expandedDetails.length === 0 ? (
                              <p className="text-sm text-gray-500 text-center py-2">Sin detalles</p>
                            ) : (
                              <div className="space-y-4">
                                {groupByClient(expandedDetails).map((group, gIdx) => (
                                  <div key={gIdx}>
                                    <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                      <Building2 size={14} className="text-gray-400" />
                                      {group.clientName}
                                    </h4>
                                    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                                      <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                          <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Producto</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Referencia</th>
                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">P. Coste</th>
                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Precio Anterior</th>
                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Nuevo Precio</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                          {group.items.map((item, idx) => (
                                            <tr key={idx}>
                                              <td className="px-3 py-2 text-sm text-gray-900">
                                                {item.producto_nombre || item.nombre || '—'}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-500 font-mono">
                                                {item.producto_raiz || item.referencia || item.raiz || '—'}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600 text-right font-mono">
                                                {item.producto_precio_coste != null ? `${Number(item.producto_precio_coste).toFixed(2)} €` : '—'}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-500 text-right">
                                                {item.precio_anterior != null ? formatCurrency(item.precio_anterior) : <span className="text-gray-300">Sin precio</span>}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-right font-bold" style={{ color: PRIMARY }}>
                                                {formatCurrency(item.precio_propuesto)}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                ))}

                                {/* Inline approve/reject for expanded view */}
                                {isPending && (
                                  <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
                                    <button
                                      onClick={() => handleApprove(offer.id)}
                                      disabled={actionLoading === offer.id}
                                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors"
                                    >
                                      {actionLoading === offer.id ? (
                                        <Loader2 size={14} className="animate-spin" />
                                      ) : (
                                        <Check size={14} />
                                      )}
                                      Aprobar Oferta
                                    </button>
                                    <button
                                      onClick={() => { setRejectModal(offer.id); setRejectMotivo(''); }}
                                      disabled={actionLoading === offer.id}
                                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
                                    >
                                      <X size={14} />
                                      Rechazar Oferta
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500">
              Mostrando {((page - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(page * ITEMS_PER_PAGE, totalOffers)} de {totalOffers}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-md border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-gray-700 font-medium px-2">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-md border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ═══ REJECT MODAL ═══ */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={() => setRejectModal(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Rechazar Oferta #{rejectModal}</h2>
              <button onClick={() => setRejectModal(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo del rechazo (opcional)</label>
              <textarea
                value={rejectMotivo}
                onChange={(e) => setRejectMotivo(e.target.value)}
                rows={3}
                placeholder="Indica el motivo del rechazo..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setRejectModal(null)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading === rejectModal}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading === rejectModal && <Loader2 size={16} className="animate-spin" />}
                Confirmar Rechazo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOfertas;
